<template>
  <v-dialog v-model="editCategoryDialog" persistent :width="width">
    <v-card>
      <v-toolbar color="#EEF2F6" max-height="64px">
        <v-card-title>
          <i class="fal fa-cabinet-filing mr-2" />
          <span class="headline">Edit {{ category.name }}</span>
        </v-card-title>
        <v-spacer />
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="btn btn-light btn-elevate-hover btn-circle btn-icon btn-sm mr-2"
              v-bind="attrs"
              v-on="on"
              readonly=""
              @click="toggleHelperDialog"
            >
              <i class="fas fa-question" />
            </button>
          </template>
          <span>More Information</span>
        </v-tooltip>
        <button
          type="button"
          class="btn btn-light btn-elevate-hover btn-circle btn-icon btn-sm"
          @click="
            revertToOriginal();
            close();
          "
        >
          <i class="fal fa-times" />
        </button>
      </v-toolbar>

      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" sm="12" md="12" v-show="showCurriculum">
              <v-autocomplete
                v-model="category.curriculum_id"
                :disabled="isLoading"
                :loading="isLoading"
                name="curriculum"
                label="Select Curriculum"
                :items="curriculums"
                :item-text="(item) => item.name"
                :item-value="(item) => item.id"
                :menu-props="{ closeOnClick: true }"
              >
                <template v-slot:selection="data">
                  {{ data.item.name }} &nbsp;
                  <span
                    :class="
                      data.item.is_draft
                        ? 'text-small'
                        : 'text-success text-small'
                    "
                    >({{ data.item.is_draft ? 'Draft' : 'Published' }})</span
                  >
                </template>
                <template v-slot:item="data">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ data.item.name }} &nbsp;
                      <span
                        :class="
                          data.item.is_draft
                            ? 'text-small'
                            : 'text-success text-small'
                        "
                        >({{
                          data.item.is_draft ? 'Draft' : 'Published'
                        }})</span
                      >
                    </v-list-item-title>
                    <v-list-item-subtitle
                      v-html="data.item.company_author.name"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" sm="12" md="12">
              <v-text-field
                v-model="category.name"
                label="Course Name"
                name="name"
                v-validate="'required'"
                :disabled="isLoading"
                :loading="isLoading"
              ></v-text-field>
              <span
                class="kt-font-danger validate-error"
                v-show="errors.has('name')"
                >{{ errors.first('name') }}</span
              >
            </v-col>
            <v-col cols="12" sm="12" md="12">
              <v-text-field
                name="fileUploadOrLink"
                v-model="thumbnail"
                label="Thumbnail"
                @click="toggleUploadDialog"
              >
              </v-text-field>
              <v-btn
                color="primary"
                text
                small
                v-if="!showImage"
                @click="showImage = true"
              >
                Show Current Image
              </v-btn>
              <v-btn
                color="primary"
                text
                small
                v-else
                @click="showImage = false"
              >
                Hide Current Image
              </v-btn>
              <v-img
                class="mt-2"
                v-if="showImage"
                aspect-ratio="1.6"
                :src="category.thumbnail"
              />
              <span
                class="kt-font-danger validate-error"
                v-show="errors.has('thumbnail')"
                >{{ errors.first('thumbnail') }}</span
              >
            </v-col>
            <v-col cols="12" sm="12" md="12">
              <v-switch
                :disabled="isLoading"
                :loading="isLoading"
                v-model="category.is_draft"
                :label="`Draft`"
              ></v-switch>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="blue-grey darken-1"
          text
          :disabled="isLoading"
          :loading="isLoading"
          @click="
            revertToOriginal();
            close();
          "
          >Cancel</v-btn
        >
        <v-btn
          color="blue darken-1"
          text
          :disabled="isLoading"
          :loading="isLoading"
          @click="update"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
    <helper-dialog
      :close-dialog="closeConfirmDialog"
      :show-dialog="showHelperDialog"
    >
      <ul>
        <li>
          <strong>Curriculum</strong> refers to the set of course(s). Field is
          where course is assigned to
        </li>
        <li>
          <strong>Course Name</strong> Course(s) refers to the set of module(s).
          This are the box that appears on your homepage
        </li>
        <li>
          <strong>Image</strong> refers to the image/thumbnail that appears on
          box of your certification section.
        </li>
        <li>
          <strong>Draft</strong> refers to the status if you want your course to
          be displayed on your homepage.
        </li>
      </ul>
    </helper-dialog>
    <upload-file-dialog
      :type="['image']"
      :show-upload-dialog="showUploadDialog"
      :upload-dialog="toggleUploadDialog"
      :close-dialog="closeUploadDialog"
      v-on:showImage="getThumbnail"
    />
  </v-dialog>
</template>

<script>
import UploadFileDialog from '@/components/dialogs/UploadFileDialog';
import HelperDialog from '@/components/dialogs/HelperDialog';

export default {
  name: 'EditCategoryDialog',
  components: {
    HelperDialog,
    UploadFileDialog,
  },
  props: {
    curriculum_id: Number,
    curriculums: Array,
    category: Object,
    editCategoryDialog: Boolean,
    toggleEditCategoryDialog: Function,
    showCurriculum: Boolean,
  },
  data() {
    return {
      showUploadDialog: false,
      showHelperDialog: false,
      thumbnail: null,
      isLoading: false,
      showImage: false,
      originalData: {
        name: this.category.name,
        thumbnail: this.category.thumbnail,
        is_draft: this.category.is_draft,
      },
    };
  },
  watch: {
    editCategoryDialog(val) {
      // eslint-disable-next-line
      val || this.close();
    },
  },
  methods: {
    revertToOriginal() {
      this.category.name = this.originalData.name;
      this.category.thumbnail = this.originalData.thumbnail;
      this.category.is_draft = this.originalData.is_draft;
    },
    toggleHelperDialog() {
      this.showHelperDialog = true;
    },
    closeConfirmDialog() {
      this.showHelperDialog = false;
    },
    toggleUploadDialog() {
      this.showUploadDialog = true;
    },
    closeUploadDialog() {
      this.showUploadDialog = false;
    },
    getThumbnail(item) {
      this.thumbnail = item.value;
    },
    formData() {
      const form = new FormData();
      form.append('id', this.category.id);
      form.append('name', this.category.name);
      //create a method that would get the course id
      form.append('course', 1);
      form.append('curriculum_id', this.category.curriculum_id);
      if (this.thumbnail) {
        form.append('thumbnail', this.thumbnail);
      } else {
        form.append('thumbnail', this.category.thumbnail);
      }
      form.append('is_draft', this.category.is_draft);
      return form;
    },

    update() {
      this.isLoading = true;
      const formData = this.formData();
      this.$store
        .dispatch('categories/editCategory', {
          payload: formData,
          id: this.category.id,
        })
        .then((response) => {
          this.$notify('success', this.category.name + ' updated!');
          let data = {
            course: response.data,
            old_curriculum_id: this.curriculum_id,
            move_curriculum:
              this.curriculum_id !== parseInt(response.data.curriculum_id),
          };
          this.$emit('updateCourse', data);
          this.isLoading = false;
          this.close();
        })
        .catch((error) => {
          this.interactWithErrorBag(error, this.errors);
        })
        .finally((data) => {
          this.isLoading = false;
        });
    },
    close() {
      this.editCategoryDialog = false;
      (this.thumbnail = []), (this.isLoading = false);
      this.$emit('close-dialog');
    },
  },
  computed: {
    width() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return '100%';
        case 'sm':
          return '100%';
        default:
          return '50%';
      }
    },
  },
};
</script>

<style scoped>
@media only screen and (max-width: 600px) {
  .headline {
    font-size: 10px !important;
  }
}
</style>
